import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Typography, Card, Row, Col, Button, message, Spin, Space } from 'antd';
import { ShopOutlined, LogoutOutlined, EnvironmentOutlined } from '@ant-design/icons';
import type { RootState } from 'sama-shared';
import { setActiveStore, setRolePermissions, logout } from 'sama-shared';
import { useLazyGetRolePermissionsQuery } from 'sama-shared';
import { useBrasa, isDarkTheme } from '../../../components/ui/brasaTheme';

const { Title, Text } = Typography;

export const SelectStoreView: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const allowedStores = useSelector((state: RootState) => state.auth.allowedStores);
  const partner = useSelector((state: RootState) => state.auth.partner);
  const currentTheme = useSelector((state: RootState) => (state as any).auth?.theme ?? 'dark');
  const b = useBrasa();
  const isDark = isDarkTheme(currentTheme);

  const [triggerGetPermissions] = useLazyGetRolePermissionsQuery();
  const [selectingId, setSelectingId] = useState<string | null>(null);

  const handleSelectStore = async (storeId: string) => {
    setSelectingId(storeId);
    try {
      const res = await triggerGetPermissions(storeId).unwrap();
      dispatch(setActiveStore(storeId));
      dispatch(setRolePermissions({ role: res.role, permissions: res.permissions }));
      message.success('Sucursal seleccionada correctamente');
      navigate('/orders');
    } catch (err: any) {
      console.error('Error fetching permissions:', err);
      message.error(err?.data?.message || 'Error al obtener los permisos de la sucursal.');
    } finally {
      setSelectingId(null);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background:
          isDarkTheme(currentTheme) && currentTheme !== 'ocean'
            ? 'radial-gradient(1000px 500px at 20% 10%, rgba(255,90,0,0.16), transparent 60%), radial-gradient(800px 500px at 85% 90%, rgba(255,176,32,0.10), transparent 60%), #100c09'
            : currentTheme === 'ocean'
              ? 'radial-gradient(1000px 500px at 20% 10%, rgba(255,90,0,0.12), transparent 60%), radial-gradient(800px 500px at 85% 90%, rgba(56,189,248,0.10), transparent 60%), #0a1222'
              : 'radial-gradient(1000px 500px at 20% 10%, rgba(255,90,0,0.12), transparent 60%), radial-gradient(800px 500px at 85% 90%, rgba(255,176,32,0.12), transparent 60%), #fff3e6',
        color: b.tx1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        position: 'relative',
      }}
    >
      <Button
        type="text"
        icon={<LogoutOutlined />}
        onClick={handleLogout}
        style={{
          position: 'absolute',
          top: 24,
          right: 24,
          color: b.tx3,
          fontSize: '15px',
        }}
        className="logout-btn"
      >
        Cerrar sesión
      </Button>

      <div
        style={{
          width: '100%',
          maxWidth: 800,
          textAlign: 'center',
          marginBottom: 32,
        }}
      >
        <Title
          level={2}
          className="sama-display"
          style={{
            margin: 0,
            fontWeight: 700,
            background: 'linear-gradient(135deg,#ff5a00,#ffb020)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
          }}
        >
          {partner?.name || 'SAMA E-commerce'}
        </Title>
        <Title level={4} style={{ color: b.tx1, marginTop: 8, marginBottom: 12, fontWeight: 500 }}>
          Selecciona una sucursal para ingresar
        </Title>
        <Text type="secondary" style={{ color: b.tx3, fontSize: '14px' }}>
          Tu perfil tiene acceso a las siguientes sedes. Selecciona una para continuar:
        </Text>
      </div>

      <div
        style={{
          width: '100%',
          maxWidth: 800,
          maxHeight: '60vh',
          overflowY: 'auto',
          padding: '8px',
          borderRadius: 12,
          backgroundColor: b.bgInset,
          border: `1px solid ${b.line}`,
        }}
        className="scrollable-container"
      >
        {allowedStores.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <ShopOutlined style={{ fontSize: 48, color: '#ff6c00', marginBottom: 16 }} />
            <Title level={4} style={{ color: b.tx1 }}>
              No tienes sucursales asignadas
            </Title>
            <Text type="secondary" style={{ color: b.tx3 }}>
              Por favor, ponte en contacto con tu administrador para obtener acceso.
            </Text>
          </div>
        ) : (
          <Spin spinning={selectingId !== null} tip="Cargando permisos..." size="large">
            <Row gutter={[16, 16]} style={{ margin: 0 }}>
              {allowedStores.map((store) => (
                <Col xs={24} sm={12} key={store.id}>
                  <Card
                    hoverable
                    onClick={() => handleSelectStore(store.id)}
                    style={{
                      backgroundColor: b.bgBase,
                      borderColor: b.line,
                      borderRadius: 20,
                      transition: 'all 0.25s ease',
                      boxShadow: isDark
                        ? '0 4px 12px rgba(0,0,0,0.4)'
                        : '0 4px 12px rgba(120,72,20,0.08)',
                    }}
                    styles={{ body: { padding: 24 } }}
                    className="store-card"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 12,
                          backgroundColor: 'rgba(255, 108, 0, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <ShopOutlined style={{ fontSize: 22, color: '#ff6c00' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Title
                          level={5}
                          className="sama-display"
                          style={{
                            color: b.tx1,
                            margin: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {store.name}
                        </Title>
                        {store.mapsUrl && (
                          <Space style={{ marginTop: 6 }} size={4}>
                            <EnvironmentOutlined style={{ color: '#ff6c00', fontSize: 12 }} />
                            <Text type="secondary" style={{ fontSize: 12, color: b.tx3 }}>
                              Ver ubicación
                            </Text>
                          </Space>
                        )}
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Spin>
        )}
      </div>

      <style>{`
        .scrollable-container::-webkit-scrollbar {
          width: 6px;
        }
        .scrollable-container::-webkit-scrollbar-track {
          background: ${b.bgInset};
          border-radius: 12px;
        }
        .scrollable-container::-webkit-scrollbar-thumb {
          background: ${b.line};
          border-radius: 12px;
        }
        .scrollable-container::-webkit-scrollbar-thumb:hover {
          background: #ff6c00;
        }
        .store-card:hover {
          border-color: #ff6c00 !important;
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(255, 108, 0, 0.15) !important;
        }
        .logout-btn:hover {
          color: ${b.tx1} !important;
          background: ${isDark ? 'rgba(255,214,170,0.06)' : 'rgba(120,72,20,0.06)'} !important;
        }
      `}</style>
    </div>
  );
};

export default SelectStoreView;
